/**
 * Curated domain lists for email autocomplete and typo detection. Kept small
 * on purpose: suggestions must be predictable, and the typo checker loses
 * accuracy as the candidate list grows.
 */

const GLOBAL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "aol.com",
  "live.com",
  "msn.com",
  "proton.me",
  "protonmail.com",
  "mail.com",
  "gmx.com",
  "yandex.com",
  "zoho.com",
  "me.com",
  "comcast.net",
  "att.net",
  "verizon.net",
] as const;

/** Regional providers, keyed by primary language subtag ("tr", not "tr-TR"). */
const DOMAINS_BY_LANGUAGE: Record<string, string[]> = {
  tr: ["hotmail.com.tr", "yandex.com.tr", "mynet.com"],
  de: ["web.de", "gmx.de", "gmx.net", "t-online.de", "freenet.de"],
  fr: ["orange.fr", "free.fr", "laposte.net", "sfr.fr", "wanadoo.fr"],
  pl: ["wp.pl", "o2.pl", "onet.pl", "interia.pl"],
  it: ["libero.it", "virgilio.it", "tiscali.it"],
};

export function getEmailDomains(language?: string): string[] {
  const lang = language?.toLowerCase().split(/[-_]/)[0] ?? "";
  const regional = DOMAINS_BY_LANGUAGE[lang] ?? [];
  // Regional providers surface right after the global top 5 so they show up
  // for the bare-"@" suggestions, but never above gmail/yahoo/hotmail.
  return [...GLOBAL_DOMAINS.slice(0, 5), ...regional, ...GLOBAL_DOMAINS.slice(5)];
}

/**
 * Extra inputs for @zootools/email-spell-checker: lets it fix a typo in just
 * the provider ("gmail.cmo") or just the TLD ("gmail.con") independently.
 * An exact match on BOTH lists means "no typo", so every legitimate provider
 * name must be here or it gets "corrected" (e.g. mail.ru → gmail.ru).
 */
export const EMAIL_SECOND_LEVEL_DOMAINS = [
  "gmail",
  "yahoo",
  "hotmail",
  "outlook",
  "icloud",
  "aol",
  "live",
  "msn",
  "proton",
  "protonmail",
  "mail",
  "gmx",
  "yandex",
  "zoho",
  "me",
  "comcast",
  "att",
  "verizon",
  // regional providers (see DOMAINS_BY_LANGUAGE)
  "web",
  "t-online",
  "freenet",
  "orange",
  "free",
  "laposte",
  "sfr",
  "wanadoo",
  "wp",
  "o2",
  "onet",
  "interia",
  "libero",
  "virgilio",
  "tiscali",
  "mynet",
];

export const EMAIL_TOP_LEVEL_DOMAINS = [
  "com",
  "net",
  "org",
  "edu",
  "info",
  "io",
  "me",
  "co",
  "co.uk",
  "uk",
  "de",
  "fr",
  "it",
  "es",
  "pl",
  "nl",
  "be",
  "ch",
  "at",
  "se",
  "no",
  "dk",
  "fi",
  "cz",
  "sk",
  "ro",
  "hu",
  "gr",
  "pt",
  "tr",
  "com.tr",
  "ru",
  "ua",
  "ca",
  "us",
  "mx",
  "br",
  "com.br",
  "au",
  "com.au",
  "email",
  "online",
];
